import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../services/api";
import "./Read.scss";

// Hooks
import { useBookData } from "../hooks/useBookData";
import { useAudioPlayer } from "../hooks/useAudioPlayer";
import { useSelection } from "../hooks/useSelection";
import { useReadingSettings } from "../hooks/useReadingSettings";
import { useTheme } from "../context/ThemeContext";

// Components
import { TopBar } from "../components/Read/TopBar";
import { BottomBar } from "../components/Read/BottomBar";
import { ContextToolbar } from "../components/Read/ContextToolbar";
import { SettingsModal } from "../components/Read/SettingsModal";

// Helper
const getRelativeCoordinates = (root, element) => {
  const rect = element.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  return {
    x: rect.left - rootRect.left,
    y: rect.top - rootRect.top,
  };
};

// Note Styles Configuration, keyed by theme mode since these need to stay
// visually distinguishable from each other regardless of the active colorway.
const noteStylesByMode = {
  light: {
    highlight: { border: "border-yellow-500", bg: "bg-yellow-50", iconColor: "text-yellow-600" },
    definition: { border: "border-blue-500", bg: "bg-blue-50", iconColor: "text-blue-600" },
    example: { border: "border-green-500", bg: "bg-green-50", iconColor: "text-green-600" },
  },
  dark: {
    highlight: { border: "border-yellow-500", bg: "bg-yellow-500/10", iconColor: "text-yellow-400" },
    definition: { border: "border-blue-500", bg: "bg-blue-500/10", iconColor: "text-blue-400" },
    example: { border: "border-green-500", bg: "bg-green-500/10", iconColor: "text-green-400" },
  },
};

const Read = () => {
  const { readingKey } = useParams();
  const navigate = useNavigate();
  const bookContentRef = useRef(null);
  const { mode } = useTheme();
  const noteStyles = noteStylesByMode[mode] || noteStylesByMode.dark;

  // Data & State Hooks
  const {
    reading, sections, toc, allNotes, entities, currentSectionIndex, sectionHtml,
    addNote, removeNote, addEntity, goToNextSection, goToPreviousSection
  } = useBookData(readingKey);

  const audioPlayer = useAudioPlayer();

  const {
    selection, setSelection, clearSelection, handleWordClick, handleMouseUp
  } = useSelection(bookContentRef);

  const settings = useReadingSettings();
  const { fontSize, showNotesPanel, toggleNotesPanel } = settings;

  // Local State
  const [sectionNotes, setSectionNotes] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // --- DOM Injection & Decoration ---

  // 1. Inject HTML Content
  useEffect(() => {
    if (bookContentRef.current) {
      bookContentRef.current.innerHTML = sectionHtml;
    }
  }, [sectionHtml]);

  // Audio Element Setup
  useEffect(() => {
    if (audioPlayer.audioRef.current) {
        audioPlayer.audioRef.current.onended = () => audioPlayer.togglePlayPause();
    }
  }, [audioPlayer.audioRef, audioPlayer.togglePlayPause]);

  // 2. Apply Highlights (Notes & Entities)
  const applyDecorations = useCallback(() => {
    if (!bookContentRef.current) return;

    // Helper to find valid word indices in current section
    const wordElements = bookContentRef.current.querySelectorAll('[data-w]');
    const sectionWordIndices = new Set();
    wordElements.forEach(el => sectionWordIndices.add(parseInt(el.dataset.w)));

    // Filter notes belonging to this section
    const notesInSection = allNotes.filter(note => {
      for (let i = note.start_word; i <= note.end_word; i++) {
        if (sectionWordIndices.has(i)) return true;
      }
      return false;
    });

    // Apply visual styles for notes
    const processedNotes = notesInSection.map(note => {
        let startCoords = null;
        let endCoords = null;

        for (let i = note.start_word; i <= note.end_word; i++) {
            const span = bookContentRef.current.querySelector(`[data-w="${i}"]`);
            if (span) {
                span.classList.add(`note-${note.type}`);
                if (i === note.start_word) startCoords = getRelativeCoordinates(bookContentRef.current, span);
                if (i === note.end_word) endCoords = getRelativeCoordinates(bookContentRef.current, span);
            }
        }
        return { ...note, start: startCoords, end: endCoords };
    });

    setSectionNotes(processedNotes);

    // Apply Entity Highlights
    // Clear existing
    bookContentRef.current.querySelectorAll('.entity-character, .entity-concept').forEach(el => {
      el.classList.remove('entity-character', 'entity-concept');
    });

    // Apply new
    entities.forEach(entity => {
      const entityWords = entity.title.toLowerCase().split(' ');
      const firstWord = entityWords[0];

       bookContentRef.current.querySelectorAll(`[data-w]`).forEach(span => {
        if (span.textContent.toLowerCase() === firstWord) {
             let match = true;
            let currentSpan = span;
            // Check subsequent words
             for(let i=1; i<entityWords.length; i++) {
                const nextIndex = parseInt(currentSpan.dataset.w) + 1;
                const nextSpan = bookContentRef.current.querySelector(`[data-w="${nextIndex}"]`);
                if(!nextSpan || nextSpan.textContent.toLowerCase() !== entityWords[i]) {
                   match = false; break;
              }
              currentSpan = nextSpan;
            }

             if (match) {
                const startIdx = parseInt(span.dataset.w);
                for(let i=0; i<entityWords.length; i++) {
                   const wSpan = bookContentRef.current.querySelector(`[data-w="${startIdx + i}"]`);
                   if (wSpan && !wSpan.className.includes('note-')) {
                      wSpan.classList.add(`entity-${entity.type}`);
                   }
            }
          }
        }
      });
    });

  }, [allNotes, entities]);

  // Re-apply decorations when HTML content updates or data changes
  useEffect(() => {
      applyDecorations();
  }, [applyDecorations, sectionHtml]);


  // --- Handlers ---

  const handleCreateNote = async (type) => {
    if (!selection.startWord || !selection.endWord) return;
    await addNote({
      readingKey,
      type,
      text: selection.text,
      startWord: selection.startWord,
      endWord: selection.endWord,
    });
    clearSelection();
  };

  const handleCreateEntity = async (type) => {
    if (!selection.text) return;
    await addEntity({ type, text: selection.text });
    clearSelection();
  };

  const getSectionTitle = (index) => {
      if (!sections[index]) return "";
    const findInToc = (items) => {
      for (const item of items) {
              if (item.href === `#${sections[index].id}`) return item.title;
        if (item.items) {
          const found = findInToc(item.items);
          if (found) return found;
        }
      }
      return null;
      };
      return findInToc(toc) || `Chapter ${index + 1}`;
    };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen();
    else if (document.exitFullscreen) document.exitFullscreen();
  };

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden" style={{ background: 'var(--background)' }}>
      {/* External Styles */}
      {reading?.styles.map((style, i) => (
        <link key={i} rel="stylesheet" href={`${API_BASE_URL}${style}`} />
      ))}

      {/* Top Bar - Structural */}
      <TopBar
        bookTitle={reading?.title}
        chapterTitle={getSectionTitle(currentSectionIndex)}
        onHome={() => navigate('/')}
        onFullscreen={toggleFullscreen}
      />

      {/* Middle Content - Flex Grow */}
      <div className="flex-1 relative w-full overflow-hidden">
         <audio
            ref={audioPlayer.audioRef}
            onTimeUpdate={audioPlayer.handleTimeUpdate}
            onLoadedMetadata={audioPlayer.handleLoadedMetadata}
            hidden
         />
         <div className="h-full w-full overflow-y-auto scroll-smooth custom-scrollbar">
            <div className="min-h-full px-4 py-8 md:px-8 lg:px-12 flex justify-center">

               <div className={`relative transition-all duration-500 ease-in-out flex gap-6 ${showNotesPanel ? 'w-full max-w-6xl' : 'w-full'}`}>

                  {/* Book Text */}
                  <div className={`flex-1 transition-all duration-500 ${showNotesPanel ? 'lg:w-2/3' : 'w-full'}`}>
                      <div
                        className="book-wrapper relative"
                        style={{ fontSize: `${fontSize}rem` }}
                      >
                        <div
                          ref={bookContentRef}
                          onClick={handleWordClick}
                          onMouseUp={handleMouseUp}
                          className="book-content leading-loose"
                          style={{ color: 'var(--foreground)' }}
                        />

                        {/* Indicators */}
                        {showNotesPanel && sectionNotes.map(note => note.start && note.end && (
                           <div key={`ind-${note.id}`} className="pointer-events-none">
                              <div className={`note-circle note-circle-start note-circle-${note.type}`} style={{ left: note.start.x - 4, top: note.start.y + 8 }} />
                              <div className={`note-circle note-circle-end note-circle-${note.type}`} style={{ left: note.end.x - 4, top: note.end.y + 8 }} />
                        </div>
                        ))}
                  </div>
                </div>

                  {/* Notes Sidebar */}
                  {showNotesPanel && (
                     <div className="hidden lg:block w-80 xl:w-96 shrink-0 relative">
                        {sectionNotes.map(note => note.start && (
                           <div
                             key={note.id}
                             className="absolute left-0 w-full transition-all duration-500"
                             style={{ top: note.start.y }}
                           >
                              <div className={`group relative p-3 rounded-xl border shadow-sm hover:shadow-md transition-shadow ${noteStyles[note.type].border} ${noteStyles[note.type].bg}`}>
                      <button
                                    onClick={() => removeNote(note.id)}
                                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                      >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                                 <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-bold uppercase ${noteStyles[note.type].iconColor}`}>{note.type}</span>
                    </div>
                                 <p className="text-sm leading-relaxed font-serif" style={{ color: 'var(--foreground)' }}>{note.text}</p>
                    </div>
                  </div>
                        ))}
                    </div>
                  )}

                      </div>
                    </div>
                  </div>
                </div>

      {/* Bottom Section - Stacked Column */}
      <div className="shrink-0 flex flex-col w-full z-50">
        {/* Contextual Toolbar (Selection/Translation) - Appears above BottomBar */}
        <ContextToolbar
          selection={selection}
          onClear={clearSelection}
          onCreateNote={handleCreateNote}
          onCreateEntity={handleCreateEntity}
        />

        {/* Bottom Control Bar */}
        <BottomBar
          onPrev={goToPreviousSection}
          onNext={goToNextSection}
          hasPrev={currentSectionIndex > 0}
          hasNext={currentSectionIndex < sections.length - 1}
          audioPlayer={audioPlayer}
          showNotesPanel={showNotesPanel}
          toggleNotesPanel={toggleNotesPanel}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />
      </div>

      {/* Settings Modal (Fixed Overlay) */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
      />

    </div>
  );
};

export default Read;
